import { useForm } from 'antd/lib/form/Form';
import React, { FC, useCallback, useMemo, useState } from 'react';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { Alert, Form, Input, InputNumber, message, Select, Switch } from 'antd';
import { equipmentTypesRestCrudThunksForSupplier } from '../../thunks/equipment_types_thunks';
import { stockLocationsRestCrudThunksForSupplier } from '../../thunks/stock_locations_thunks';
import { equipmentPerStockLocationsRestCrudThunksForSupplier } from '../../thunks/equipment_per_stock_locations_thunks';
import { vendorsRestCrudThunksForSupplier } from '../../thunks/vendors_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import { OWButton } from '../ow_button/OWButton';
import { renderEquipmenTypeSelect } from '../purchase_order_form/ReplaceEquipmentLineItemModal';
import { connect } from 'react-redux';
import BackButton from '../back_button/BackButton';
import { StockLocationDropdownRecord } from '../StockLocationDropdownRecord';
import {
	ALL_EPSL_CONDITIONS,
	ALL_EPSL_DISPOSITION_PLANS,
	EPSL_CONDITION_DISPLAY_NAMES,
	EPSL_DISPOSITION_PLAN_DISPLAY_NAMES,
} from '../../utils/stockLocationUtils';

const FORM_ID = 'add-equipment-form';
const FormItem = Form.Item;
const PURCHASE_ORDER_FORM_STOCK_LOCATION_DROPDOWN = 'purchaseOrderFormStockLocationDropdown';

const AddOrEditEquipmentPerStockLocationForm: FC<any> = ({
	createErrors,
	updateErrors,
	formData,
	fetchEquipment,
	equipmentTypes,
	fetchEquipmentTypes,
	fetchMultipleEquipments,
	stockLocations,
	fetchStockLocations,
	fetchMultipleStockLocations,
	stockLocationId,
	createEquipment,
	updateEquipment,
	onSuccess,
	vendors,
	fetchVendors,
	fetchMultipleVendors,
}): React.ReactElement => {
	const [form] = useForm();

	const { setFieldsValue } = form;

	const [saving, setSaving] = useState(false);

	const isEdit = useMemo(() => nullSafeGet('id', formData), [formData]);

	const initialValues = useMemo(
		() => ({
			...formData,
			assetNumber: nullSafeGetOrElse('asset.assetNumber', formData, formData.assetNumber),
		}),
		[formData]
	);

	const handleEquipmentItemChange = useCallback(
		(equipmentTypeId) => {
			fetchEquipment(equipmentTypeId).then((eq) => {
				if (eq.cost) {
					setFieldsValue({ [`cost`]: eq.cost.toString() });
				} else {
					setFieldsValue({ [`cost`]: undefined });
				}
				setFieldsValue({ [`name`]: eq.modelName });
				setFieldsValue({ [`equipmentType`]: eq });
			});
		},
		[fetchEquipment, setFieldsValue]
	);

	const submitAction = useMemo(
		() => (isEdit ? updateEquipment : createEquipment),
		[createEquipment, isEdit, updateEquipment]
	);

	const onSubmit = useCallback(
		(values) => {
			const equipmentPerStockLocationId = {
				...formData,
				...values,
				...(values.cost && { cost: parseFloat(values.cost) }),
				name: values.name,
				images: values.equipmentType.images,
				isRental: !!values.isRental,
				...(!!stockLocationId && { stockLocationId: parseInt(stockLocationId) }),
			};

			setSaving(true);
			submitAction(equipmentPerStockLocationId)
				.then((epsl) => {
					onSuccess(epsl);
					message.success(`Equipment saved successfully!`);
				})
				.catch((err) => message.error(err))
				.finally(() => setSaving(false));
		},
		[formData, onSuccess, stockLocationId, submitAction]
	);

	return (
		<Form
			form={form}
			id={FORM_ID}
			layout="vertical"
			initialValues={initialValues}
			onFinish={onSubmit}
			style={{ width: 640, maxWidth: 640 }}
		>
			{createErrors.length > 0 ? (
				<FormItem>
					<Alert message={createErrors.join(' ')} type="error" />
				</FormItem>
			) : null}
			{updateErrors.length > 0 ? (
				<FormItem>
					<Alert message={updateErrors.join(' ')} type="error" />
				</FormItem>
			) : null}
			<Form.Item name="equipmentType" hidden />
			{!stockLocationId && (
				<FormItem
					name="stockLocationId"
					label="Stock Location"
					rules={[{ required: true, message: 'Stock location is required.' }]}
				>
					<OWAsyncSelect
						stateSlice={stockLocations}
						targetCollectionName={PURCHASE_ORDER_FORM_STOCK_LOCATION_DROPDOWN}
						fetchMultiple={(ids, targetCollectionName) => {
							fetchMultipleStockLocations(ids, targetCollectionName);
						}}
						fetchData={(
							searchText,
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						) => {
							fetchStockLocations(
								{ name: searchText },
								targetCollectionName,
								pagination,
								sorting,
								filters,
								addToTargetCollection
							);
						}}
						renderRecord={(record) => (
							<Select.Option key={`${record.id} - ${record.name}`} value={record.id}>
								<StockLocationDropdownRecord record={record} />
							</Select.Option>
						)}
					/>
				</FormItem>
			)}
			<FormItem
				name="equipmentTypeId"
				label="Equipment Type"
				rules={[{ required: true, message: 'Please select an equipment type.' }]}
			>
				<OWAsyncSelect
					stateSlice={equipmentTypes}
					targetCollectionName="equipmentTypesAtStockLocationIndex"
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleEquipments(ids, targetCollectionName);
					}}
					fetchData={(
						searchText,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchEquipmentTypes(
							{ name: searchText },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					onChange={handleEquipmentItemChange}
					renderRecord={renderEquipmenTypeSelect}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</FormItem>
			<FormItem name="name" label="Name">
				<Input style={{ width: '100%' }} />
			</FormItem>
			<FormItem name="serialNumber" label="Serial Number">
				<Input style={{ width: '100%' }} />
			</FormItem>
			<FormItem name="assetNumber" label="Asset Number">
				<Input style={{ width: '100%' }} />
			</FormItem>
			<FormItem name="cost" label="Purchase Price">
				<InputNumber style={{ width: '100%' }} />
			</FormItem>
			<FormItem name="isRental" label="Is Rental" valuePropName="checked">
				<Switch />
			</FormItem>
			<FormItem name="condition" label="Condition">
				<Select
					style={{ width: '100%' }}
					options={ALL_EPSL_CONDITIONS.map((c) => ({
						value: c,
						label: EPSL_CONDITION_DISPLAY_NAMES[c],
					}))}
				/>
			</FormItem>
			<FormItem name="dispositionPlan" label="Disposition Plan">
				<Select
					style={{ width: '100%' }}
					options={ALL_EPSL_DISPOSITION_PLANS.map((c) => ({
						value: c,
						label: EPSL_DISPOSITION_PLAN_DISPLAY_NAMES[c],
					}))}
				/>
			</FormItem>
			<Form.Item
				name="vendorId"
				label="Vendor"
				rules={[{ required: true, message: 'Please select a vendor.' }]}
			>
				<OWAsyncSelect
					style={{ maxWidth: 640 }}
					targetCollectionName="equipmentVendors"
					stateSlice={vendors}
					fetchMultiple={(ids, targetCollectionName) => {
						fetchMultipleVendors(ids, targetCollectionName);
					}}
					fetchData={(
						search,
						targetCollectionName,
						pagination,
						sorting,
						filters,
						addToTargetCollection
					) => {
						fetchVendors(
							{ search },
							targetCollectionName,
							pagination,
							sorting,
							filters,
							addToTargetCollection
						);
					}}
					//placeholder={'Select Vendor'}
					renderRecord={(vendor) => (
						<Select.Option key={vendor.id} value={vendor.id}>
							{vendor.name}
						</Select.Option>
					)}
					sortBy={{ sort_by: 'name', order: 'ascend' }}
				/>
			</Form.Item>
			<Form.Item>
				<BackButton buttonText="Cancel" />
				<OWButton
					type="primary"
					size="large"
					style={{ marginLeft: '16px' }}
					key="submit"
					htmlType="submit"
					form={FORM_ID}
					loading={saving}
				>
					{`${isEdit ? 'Update' : `Add`} Equipment`}
				</OWButton>
			</Form.Item>
		</Form>
	);
};

const mapStateToProps = (state) => ({
	createErrors: state.equipment_per_stock_locations.createErrors,
	updateErrors: state.equipment_per_stock_locations.updateErrors,
	creating: state.equipment_per_stock_locations.creating,
	updating: state.equipment_per_stock_locations.updating,
	stockLocations: state.stock_locations,
	vendors: state.vendors,

	equipmentTypes: state.equipment_types,
});

const mapDispatchToProps = (dispatch) => ({
	fetchEquipment: (id) => dispatch(equipmentTypesRestCrudThunksForSupplier.readOne(id)),
	fetchEquipmentTypes: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			equipmentTypesRestCrudThunksForSupplier.read(
				{ ...(params || {}), isActive: true },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleEquipments: (ids, targetCollectionName) =>
		dispatch(equipmentTypesRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchStockLocations: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			stockLocationsRestCrudThunksForSupplier.read(
				{ ...params, noIsActiveFilter: false },
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleStockLocations: (ids, targetCollectionName) =>
		dispatch(stockLocationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	createEquipment: (entity) =>
		dispatch(equipmentPerStockLocationsRestCrudThunksForSupplier.create(entity)),
	updateEquipment: (entity) =>
		dispatch(equipmentPerStockLocationsRestCrudThunksForSupplier.update(entity)),
	fetchVendors: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			vendorsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	fetchMultipleVendors: (ids, targetCollectionName) =>
		dispatch(vendorsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AddOrEditEquipmentPerStockLocationForm);
